import React from 'react';
import { Handle, Position } from 'reactflow';
import { Zap } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ParallelNodeData {
  label?: string;
  branches?: number;
}

export default function ParallelNode({ 
  data, 
  selected 
}: { 
  data: ParallelNodeData; 
  selected?: boolean 
}) {
  const branches = data.branches || 2;

  return (
    <div className={cn(
      "min-w-[160px] rounded-lg",
      "bg-card",
      "border-2 transition-all duration-fast",
      selected
        ? "border-accent-indigo shadow-glow-focus"
        : "border-accent-indigo/50 dark:border-accent-indigo/30 shadow-md hover:shadow-lg"
    )}>
      {/* Connection Handle */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !border-2 !bg-accent-indigo !border-accent-indigo"
        style={{ top: '-6px' }}
      />

      {/* Node Header */}
      <div className="flex items-center gap-2 p-3 border-b border-border dark:border-border-subtle">
        <div className="w-6 h-6 rounded bg-accent-indigo/10 dark:bg-accent-indigo/20 flex items-center justify-center">
          <Zap className="w-3.5 h-3.5 text-accent-indigo" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-foreground">
            Parallel
          </div>
          <div className="text-xs text-muted-foreground/70">
            {branches} concurrent branches
          </div>
        </div>
      </div>

      {/* Node Body */}
      <div className="p-3">
        <div className="text-xs text-muted-foreground">
          Execute branches simultaneously
        </div>
      </div>

      {/* Branch Handles */}
      <div className="relative pt-2">
        {Array.from({ length: branches }, (_, i) => (
          <Handle
            key={`branch-${i}`}
            type="source"
            position={Position.Bottom}
            id={`branch-${i}`}
            className="!w-3 !h-3 !border-2 !bg-accent-indigo !border-accent-indigo"
            style={{ 
              left: `${((i + 1) * 100) / (branches + 1)}%`,
              bottom: '-6px'
            }}
          />
        ))}
      </div>
    </div>
  );
}

